import { Stack } from '@mui/joy';
import { Trash } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { Tooltip } from 'core/elements';
import { ISelectedSpaFormValue, ITradeAgreement, TTradeAgreementsRequest } from 'quotes/types';
import { Color, Direction, Variant } from 'theme/enums';

import { useDeferredPressAction } from './useDeferredPressAction';
import { SelectSpaCostPanelWithProvider } from '../SelectSpaCostPanel';
import { IDefaultSpaAgreement } from '../utils';

interface IProps {
  // Called once on open: the request is frozen so a recalculation cannot refetch the list.
  buildRequestPayload: () => TTradeAgreementsRequest;
  onRemove: () => Promise<void> | void;
  onUse: (agreement: ITradeAgreement) => Promise<void> | void;
  onHeldAgreementOffered?: (isOffered: boolean) => void;
  // Called on either button's press, before the blur it causes reaches the field being edited.
  onPress?: () => void;
  partNumber?: string;
  selectedSpa?: ISelectedSpaFormValue | null;
  defaultSpa?: IDefaultSpaAgreement | null;
  // False leaves a held pick only its removal.
  canSelect?: boolean;
  // Why neither action can run now (the pricing engine is paused); shown as their tooltip.
  unavailableMessage?: string | null;
  disabled?: boolean;
  level?: number;
}

export const SelectSpaButton = ({
  buildRequestPayload,
  onRemove,
  onUse,
  onHeldAgreementOffered,
  onPress,
  partNumber,
  selectedSpa,
  defaultSpa,
  canSelect = true,
  unavailableMessage = null,
  disabled = false,
  level = 1,
}: IProps) => {
  const [requestPayload, setRequestPayload] = useState<TTradeAgreementsRequest | null>(null);

  const { t } = useTranslation();

  const hasSelectedSpa = !!selectedSpa?.spaCostSource;
  const isUnavailable = !!unavailableMessage;

  const onOpen = useCallback(() => {
    setRequestPayload(buildRequestPayload());
  }, [buildRequestPayload]);

  const onClose = useCallback(() => setRequestPayload(null), []);

  const onRemoveClick = useCallback(async () => {
    try {
      await onRemove();
    } catch {
      // The host's recalculation already reported the failure in its form.
    }
  }, [onRemove]);

  // One per button: only the pressed one stays clickable through the recalculation.
  const selectPress = useDeferredPressAction({ action: onOpen, onPress, disabled });
  const removePress = useDeferredPressAction({ action: onRemoveClick, onPress, disabled });

  return (
    <>
      <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 0.5 }}>
        {canSelect && (
          <Tooltip title={unavailableMessage ?? ''}>
            <Button
              text={hasSelectedSpa ? t('buttonLabelChangeSpa') : t('buttonLabelSelectSpa')}
              variant={Variant.OUTLINED}
              onMouseDown={selectPress.onMouseDown}
              onClick={selectPress.onClick}
              disabled={isUnavailable || selectPress.isDisabled}
            />
          </Tooltip>
        )}

        {/* Removing re-prices the line through the engine, so it waits for the window too. */}
        {hasSelectedSpa && (
          <Button
            icon={<Trash />}
            variant={Variant.OUTLINED}
            color={Color.DANGER}
            aria-label={t('buttonLabelRemoveSelectedSpa')}
            tooltipProps={{
              title: unavailableMessage || t('buttonLabelRemoveSelectedSpa'),
              children: null,
            }}
            onMouseDown={removePress.onMouseDown}
            onClick={removePress.onClick}
            disabled={isUnavailable || removePress.isDisabled}
          />
        )}
      </Stack>

      <SelectSpaCostPanelWithProvider
        isOpen={!!requestPayload}
        onClose={onClose}
        onUse={onUse}
        onRemove={onRemove}
        onHeldAgreementOffered={onHeldAgreementOffered}
        partNumber={partNumber}
        requestPayload={requestPayload}
        selectedSpa={selectedSpa}
        defaultSpa={defaultSpa}
        unavailableMessage={unavailableMessage}
        level={level}
      />
    </>
  );
};
